import { useEffect, useCallback, useRef } from 'react';

import { performanceOptimizer } from '../utils/performanceOptimizer';

interface PerformanceOptimizationOptions {
  enableMemoryMonitoring?: boolean;
  enableImageOptimization?: boolean;
  enableBundleAnalysis?: boolean;
  memoryThreshold?: number; // MB
}

/**
 * パフォーマンス最適化のためのカスタムフック
 */
export const usePerformanceOptimization = (options: PerformanceOptimizationOptions = {}) => {
  const {
    enableMemoryMonitoring = true,
    enableImageOptimization = true,
    enableBundleAnalysis = true,
    memoryThreshold = 100,
  } = options;

  const optimizationInterval = useRef<number | null>(null);
  const lastWarningTime = useRef<number>(0);
  const isDevelopment = process.env.NODE_ENV === 'development';

  // メモリ使用量の監視
  const monitorMemory = useCallback(() => {
    if (!enableMemoryMonitoring || !('memory' in performance)) return;

    const memory = (performance as any).memory;
    const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
    const totalMB = Math.round(memory.totalJSHeapSize / 1048576);

    // 開発環境では5分に1回のみ警告を表示
    const now = Date.now();
    const shouldWarn = isDevelopment ? now - lastWarningTime.current > 300000 : true;

    if (usedMB > memoryThreshold && shouldWarn) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ メモリ使用量が高いです: ${usedMB}MB / ${totalMB}MB`);

        // 最適化の提案（開発環境でのみ表示）
        const suggestions = [
          'コンポーネントのメモ化を実装してください',
          'useCallbackとuseMemoフックを使用してください',
          '非重要なコンポーネントの遅延読み込みを実装してください',
          '画像の遅延読み込みを最適化してください',
          'イベントリスナーとサブスクリプションをクリーンアップしてください',
          '大きなリストには仮想スクロールを実装してください',
        ];

        // eslint-disable-next-line no-console
        console.log('💡 最適化の提案:', suggestions);
        lastWarningTime.current = now;
      }
    }
  }, [enableMemoryMonitoring, memoryThreshold, isDevelopment]);

  // 画像の最適化
  const optimizeImages = useCallback(() => {
    if (!enableImageOptimization) return;

    // Intersection Observer APIを使用した画像の遅延読み込み
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                imageObserver.unobserve(img);
              }
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.1,
        }
      );

      // 遅延読み込み対象の画像を監視
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }, [enableImageOptimization]);

  // 未使用のCSSクラスの検出
  const detectUnusedCSS = useCallback(() => {
    // 開発環境でのみ実行し、1回のみ警告を表示
    if (!isDevelopment) return;

    try {
      const allClasses = new Set<string>();
      const usedClasses = new Set<string>();

      // 使用されているクラスを収集
      document.querySelectorAll('*').forEach(element => {
        element.classList.forEach(className => {
          usedClasses.add(className);
        });
      });

      // スタイルシートからクラスを収集
      Array.from(document.styleSheets).forEach(sheet => {
        try {
          Array.from(sheet.cssRules || []).forEach(rule => {
            if (rule instanceof CSSStyleRule) {
              allClasses.add(rule.selectorText);
            }
          });
        } catch (e) {
          // CORSエラーを無視
        }
      });

      const unusedClasses = Array.from(allClasses).filter(className => !usedClasses.has(className));

      if (unusedClasses.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ 未使用のCSSクラスが検出されました: ${unusedClasses.length}個`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('CSS分析中にエラーが発生しました:', error);
    }
  }, [isDevelopment]);

  // バンドルサイズの分析
  const analyzeBundle = useCallback(() => {
    if (!enableBundleAnalysis) return;

    // 動的インポートの使用状況をチェック
    const lazyComponents = document.querySelectorAll('[data-lazy]');
    const totalLazyComponents = lazyComponents.length;

    if (totalLazyComponents > 0 && isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`📦 遅延読み込みコンポーネント: ${totalLazyComponents}個`);
    }

    // 未使用のCSSクラスの検出（開発環境でのみ1回実行）
    if (isDevelopment) {
      detectUnusedCSS();
    }
  }, [enableBundleAnalysis, detectUnusedCSS, isDevelopment]);

  // パフォーマンス監査の実行
  const runPerformanceAudit = useCallback(() => {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log('🔍 パフォーマンス監査を実行中...');
    }

    try {
      const report = performanceOptimizer.performAudit();

      if (report.issues.length > 0 && isDevelopment) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ パフォーマンスの問題が検出されました:', report.issues);
      }

      if (report.recommendations.length > 0 && isDevelopment) {
        // eslint-disable-next-line no-console
        console.log('💡 最適化の提案:', report.recommendations);
      }

      return report;
    } catch (error) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error('パフォーマンス監査の実行中にエラーが発生しました:', error);
      }
      return null;
    }
  }, [isDevelopment]);

  // 定期的なパフォーマンス監視
  useEffect(() => {
    if (optimizationInterval.current) {
      clearInterval(optimizationInterval.current);
    }

    // 初回実行
    monitorMemory();
    optimizeImages();
    analyzeBundle();

    // 定期的な監視（開発環境では5分ごと、本番環境では10分ごと）
    const interval = isDevelopment ? 300000 : 600000;
    optimizationInterval.current = window.setInterval(() => {
      monitorMemory();
      optimizeImages();
      analyzeBundle();
    }, interval);

    return () => {
      if (optimizationInterval.current) {
        clearInterval(optimizationInterval.current);
      }
    };
  }, [monitorMemory, optimizeImages, analyzeBundle, isDevelopment]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (optimizationInterval.current) {
        clearInterval(optimizationInterval.current);
      }
    };
  }, []);

  return {
    monitorMemory,
    optimizeImages,
    analyzeBundle,
    runPerformanceAudit,
    detectUnusedCSS,
  };
};

export default usePerformanceOptimization;
